import React, { useContext, useState } from 'react'
import DeleteModal from 'components/delete'
import DataTable, { dataTableColumns } from 'components/data-table'
import RowActions, { RowAction } from 'components/data-table/rowActions'
import DateCell from 'components/data-table/dateCell'
import { Copy, Delete, Pencil } from 'components/icons'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { IProjectSecret } from 'types'
import SecretDialog, { ProjectSecretFormValues } from 'views/settings/components/secrets/components/SecretDialog'
import { TextLink } from 'views/settings/components/settingsCard'
import { useApplicationStore } from 'hooks/application'

interface Props {
  /** The hub's secrets; undefined while they're loading. */
  secrets: IProjectSecret[] | undefined
  loadFailed: boolean
  onRetry: () => void
  /** Resolves once the secret is deleted; rejects, having said why, if it couldn't be. */
  onDelete: (secret: IProjectSecret) => Promise<void>
  onEdit: (secret: IProjectSecret, values: ProjectSecretFormValues, closeModal: () => void) => Promise<void>
}

// The same length for every value, so the mask doesn't give away how long a secret is.
const MASK = '••••••••••••'
// Wide enough for the longest day, so the columns hold still as rows come and go.
const DATE_COLUMN = 'w-[124px]'

/** What a row's cells need from the list: what each action does. */
const SecretRows = React.createContext<{
  copy: (secret: IProjectSecret) => void
  edit: (secret: IProjectSecret) => void
  remove: (secret: IProjectSecret) => void
}>({
  copy: () => undefined,
  edit: () => undefined,
  remove: () => undefined
})

/**
 * A secret's value. A secret added here is hidden: its value is never shown again. The webhook signing
 * secret, which is to be copied into the system that checks the webhook requests, is shown, with a copy
 * button that appears on pointing at it.
 */
const ValueCell: React.FC<{ secret: IProjectSecret }> = ({ secret }) => {
  const { copy } = useContext(SecretRows)
  // A webhook signing secret that hasn't been generated yet has no value to show.
  if (!secret.value) return <span>Not set</span>
  if (secret.isReadOnly)
    return (
      <span className="group/value flex items-center gap-1">
        <span className="font-mono whitespace-nowrap">{secret.value}</span>
        <Tooltip content="Copy value">
          <Button
            internalType="transparent"
            aria-label={`Copy ${secret.name}`}
            // Shown on pointing at the value, and whenever it has focus.
            className="size-6 shrink-0 p-0 opacity-0 group-hover/value:opacity-100 focus-visible:opacity-100 [&_svg]:size-3.5"
            onClick={() => copy(secret)}
          >
            <Copy />
          </Button>
        </Tooltip>
      </span>
    )
  return (
    <span className="font-mono">
      <span className="sr-only">Hidden</span>
      <span aria-hidden="true">{MASK}</span>
    </span>
  )
}

const ActionsCell: React.FC<{ secret: IProjectSecret }> = ({ secret }) => {
  const { edit, remove } = useContext(SecretRows)
  const editAction: RowAction = { label: 'Edit', icon: <Pencil />, onSelect: () => edit(secret) }
  // The webhook signing secret belongs to the hub: its value can be changed, but it can't be deleted.
  const actions: RowAction[] = secret.isReadOnly
    ? [editAction]
    : [editAction, { label: 'Delete', icon: <Delete />, destructive: true, onSelect: () => remove(secret) }]
  // Always behind ⋯, even with Edit alone, so every row's actions are found in the same place.
  return <RowActions name={secret.name} actions={actions} menu />
}

// Set once, here: a cell is a component, so columns made afresh on each render would remount every cell.
// Not sortable, as before: the rows keep the server's order, by name with the webhook signing secret last.
const col = dataTableColumns<IProjectSecret>()
const COLUMNS = col.columns([
  col.accessor('name', {
    header: 'Name',
    enableSorting: false,
    cell: ({ getValue }) => <span className="block max-w-[220px] truncate font-medium">{getValue()}</span>
  }),
  col.accessor('value', {
    header: 'Value',
    enableSorting: false,
    enableGlobalFilter: false,
    cell: ({ row }) => <ValueCell secret={row.original} />
  }),
  col.accessor('createdAt', {
    header: 'Created',
    enableSorting: false,
    enableGlobalFilter: false,
    meta: { className: DATE_COLUMN },
    cell: ({ getValue }) => <DateCell iso={getValue()} />
  }),
  col.accessor('updatedAt', {
    header: 'Updated',
    enableSorting: false,
    enableGlobalFilter: false,
    meta: { className: DATE_COLUMN },
    cell: ({ getValue }) => <DateCell iso={getValue()} />
  }),
  col.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'w-0 text-right' },
    cell: ({ row }) => <ActionsCell secret={row.original} />
  })
])

const ProjectSecretsList: React.FC<Props> = ({ secrets, loadFailed, onRetry, onDelete, onEdit }) => {
  const { setSnackbarMessage } = useApplicationStore()
  const [editing, setEditing] = useState<IProjectSecret>()
  const [deleting, setDeleting] = useState<IProjectSecret>()

  const copy = (secret: IProjectSecret) => {
    // Started inside a promise, so a browser without the clipboard (which throws straight away) is caught too.
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(secret.value))
      .then(() => setSnackbarMessage({ status: 'success', message: 'Webhook signing secret copied to clipboard' }))
      .catch(() => setSnackbarMessage({ status: 'error', message: "The webhook signing secret couldn't be copied" }))
  }

  return (
    <>
      <SecretRows.Provider value={{ copy, edit: setEditing, remove: setDeleting }}>
        <DataTable
          label="Hub secrets"
          rowLabel={(secret) => secret.name}
          columns={COLUMNS}
          data={secrets}
          getRowId={(secret) => secret.publicId}
          empty={
            <>
              <div className="font-medium text-body">No secrets</div>
              <div className="mt-0.5">Secrets added to this hub will appear here.</div>
            </>
          }
          error={
            loadFailed && (
              <>
                <div className="font-medium text-body">Secrets couldn&apos;t be loaded</div>
                <div className="mt-1">
                  <TextLink onClick={onRetry}>Try again</TextLink>
                </div>
              </>
            )
          }
        />
      </SecretRows.Provider>
      {editing && (
        <SecretDialog
          key={editing.publicId}
          secret={editing}
          onClose={() => setEditing(undefined)}
          onSubmit={(values) => onEdit(editing, values, () => setEditing(undefined))}
        />
      )}
      {deleting && (
        <DeleteModal
          id="delete-secret-modal"
          open
          setOpen={(open) => !open && setDeleting(undefined)}
          resourceName={deleting.name}
          customHeading={`Delete ${deleting.name}?`}
          confirmLabel="Delete Secret"
          confirmByTyping={false}
          otherContent={
            <p className="text-[13px] text-body">
              Scripts that read this secret will no longer find it. A deleted secret can&apos;t be restored.
            </p>
          }
          deleteResource={() => onDelete(deleting)}
        />
      )}
    </>
  )
}

export default ProjectSecretsList
